import { TypedEventEmitter } from '@syncot/events';
const defaultCaretOffset = 0;
const defaultMatchedText = '';
const defaultItems = Object.freeze([]);
const defaultSelectedIndex = -1;
const defaultPosition = Object.freeze({
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
});
const noMatch = Object.freeze([-1, -1]);
const defaultMatch = () => noMatch;
const defaultLoad = () => defaultItems;
const defaultAccept = (item) => item.text;
/**
 * Creates an Autocomplete instace with the specified options.
 */
export function createAutocomplete({ editorAdapter, match = defaultMatch, load = defaultLoad, accept = defaultAccept, clearOnSelectionChange = true, }) {
    return new AutocompleteClass(editorAdapter, match, load, accept, clearOnSelectionChange);
}
class AutocompleteClass extends TypedEventEmitter {
    constructor(editorAdapter, _match, _load, _accept, clearOnSelectionChange) {
        super();
        this.editorAdapter = editorAdapter;
        this._match = _match;
        this._load = _load;
        this._accept = _accept;
        this.clearOnSelectionChange = clearOnSelectionChange;
        this._items = defaultItems;
        this._selectedIndex = defaultSelectedIndex;
        this._matchedText = defaultMatchedText;
        this._caretOffset = defaultCaretOffset;
        this._caretPosition = defaultPosition;
        this._editorPosition = defaultPosition;
        this._error = undefined;
        this._active = false;
        this._promise = undefined;
        this.pending = undefined;
        this.onAnimationFrame = () => {
            const action = this.pending;
            this.pending = undefined;
            this[action]();
        };
        this.onScroll = () => {
            this.updatePosition();
        };
        this.onResize = () => {
            this.updatePosition();
        };
        this.onSelectionChange = () => {
            if (this.active) {
                if (this.clearOnSelectionChange) {
                    if (this.pending !== 'matchNow') {
                        this.clear();
                    }
                }
                else {
                    if (this.pending !== 'clearNow') {
                        this.match();
                    }
                }
            }
        };
        this.onInput = () => {
            this.match();
        };
        this.onKeyDown = (_editor, event) => {
            switch (event.key) {
                case 'Enter':
                    if (this.active &&
                        this.items.length > 0 &&
                        !event.ctrlKey &&
                        !event.shiftKey &&
                        !event.altKey &&
                        !event.metaKey) {
                        event.preventDefault();
                        this.accept();
                    }
                    break;
                case 'Up': // IE11
                case 'ArrowUp':
                    if (this.active &&
                        this.items.length > 0 &&
                        !event.ctrlKey &&
                        !event.shiftKey &&
                        !event.altKey &&
                        !event.metaKey) {
                        event.preventDefault();
                        this.selectedIndex--;
                    }
                    break;
                case 'Down': // IE11
                case 'ArrowDown':
                    if (this.active &&
                        this.items.length > 0 &&
                        !event.ctrlKey &&
                        !event.shiftKey &&
                        !event.altKey &&
                        !event.metaKey) {
                        event.preventDefault();
                        this.selectedIndex++;
                    }
                    break;
                case 'Esc': // IE11
                case 'Escape':
                    if (this.active &&
                        !event.ctrlKey &&
                        !event.shiftKey &&
                        !event.altKey &&
                        !event.metaKey) {
                        event.preventDefault();
                        this.clear();
                    }
                    break;
                case 'Spacebar': // IE11
                case ' ':
                    if (event.ctrlKey &&
                        !event.shiftKey &&
                        !event.altKey &&
                        !event.metaKey) {
                        event.preventDefault();
                        this.match();
                    }
                    break;
            }
        };
        document.addEventListener('scroll', this.onScroll);
        window.addEventListener('resize', this.onResize);
        this.editorAdapter.on('input', this.onInput);
        this.editorAdapter.on('keyDown', this.onKeyDown);
        this.editorAdapter.on('scroll', this.onScroll);
        this.editorAdapter.on('resize', this.onResize);
        this.editorAdapter.on('selectionChange', this.onSelectionChange);
    }
    get items() {
        return this._items;
    }
    set items(items) {
        if (this._items !== items) {
            this._items = items;
            this.selectedIndex = 0;
            this.emitLater('items');
        }
    }
    get selectedIndex() {
        return this._selectedIndex;
    }
    set selectedIndex(selectedIndex) {
        /* tslint:disable-next-line:no-bitwise */
        const number = selectedIndex | 0;
        const divisor = this.items.length;
        const index = divisor > 0
            ? ((number % divisor) + divisor) % divisor
            : defaultSelectedIndex;
        if (this._selectedIndex !== index) {
            this._selectedIndex = index;
            this.emitLater('selectedIndex');
        }
    }
    get matchedText() {
        return this._matchedText;
    }
    set matchedText(matchedText) {
        if (this._matchedText !== matchedText) {
            this._matchedText = matchedText;
            this.emitLater('matchedText');
        }
    }
    get caretOffset() {
        return this._caretOffset;
    }
    set caretOffset(caretOffset) {
        if (this._caretOffset !== caretOffset) {
            this._caretOffset = caretOffset;
            this.emitLater('caretOffset');
        }
    }
    get caretPosition() {
        return this._caretPosition;
    }
    set caretPosition(caretPosition) {
        if (this._caretPosition !== caretPosition) {
            this._caretPosition = caretPosition;
            this.emitLater('caretPosition');
        }
    }
    get editorPosition() {
        return this._editorPosition;
    }
    set editorPosition(editorPosition) {
        if (this._editorPosition !== editorPosition) {
            this._editorPosition = editorPosition;
            this.emitLater('editorPosition');
        }
    }
    get error() {
        return this._error;
    }
    set error(error) {
        if (this._error !== error) {
            this._error = error;
            this.emitLater('error');
        }
    }
    get active() {
        return this._active;
    }
    set active(active) {
        if (this._active !== active) {
            this._active = active;
            this.emitLater('active');
        }
    }
    get loading() {
        return !!this.promise;
    }
    get promise() {
        return this._promise;
    }
    set promise(promise) {
        const oldLoading = this.loading;
        this._promise = promise;
        const newLoading = this.loading;
        if (oldLoading !== newLoading) {
            this.emitLater('loading');
        }
    }
    destroy() {
        document.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        this.editorAdapter.off('input', this.onInput);
        this.editorAdapter.off('keyDown', this.onKeyDown);
        this.editorAdapter.off('scroll', this.onScroll);
        this.editorAdapter.off('resize', this.onResize);
        this.editorAdapter.off('selectionChange', this.onSelectionChange);
        this.clear();
    }
    match() {
        if (!this.pending) {
            requestAnimationFrame(this.onAnimationFrame);
        }
        this.pending = 'matchNow';
    }
    updatePosition() {
        if (!this.pending) {
            requestAnimationFrame(this.onAnimationFrame);
            this.pending = 'updatePositionNow';
        }
    }
    clear() {
        if (!this.pending) {
            requestAnimationFrame(this.onAnimationFrame);
        }
        this.pending = 'clearNow';
    }
    accept() {
        const item = this.items[this.selectedIndex];
        if (item) {
            const text = this._accept(item);
            if (typeof text === 'string') {
                this.editorAdapter.focus();
                this.replace(text);
                this.clear();
                this.editorAdapter.focus();
            }
        }
    }
    replace(text) {
        const textBefore = this.editorAdapter.textBeforeCaret;
        const textAfter = this.editorAdapter.textAfterCaret;
        const match = this._match(textBefore, textAfter);
        const lengthBefore = match[0];
        const lengthAfter = match[1];
        if (lengthBefore < 0 || lengthAfter < 0) {
            return;
        }
        this.editorAdapter.textBeforeCaret =
            textBefore.substring(0, textBefore.length - lengthBefore) + text;
        this.editorAdapter.textAfterCaret = textAfter.substring(lengthAfter);
    }
    async matchNow() {
        const textBefore = this.editorAdapter.textBeforeCaret;
        const textAfter = this.editorAdapter.textAfterCaret;
        const match = this._match(textBefore, textAfter);
        const lengthBefore = match[0];
        const lengthAfter = match[1];
        if (lengthBefore < 0 || lengthAfter < 0) {
            this.clearNow();
            return;
        }
        const matchedText = textBefore.substring(textBefore.length - lengthBefore) +
            textAfter.substring(0, lengthAfter);
        const pomise = this.loadItems(matchedText);
        this.active = true;
        this.matchedText = matchedText;
        this.caretOffset = lengthBefore;
        this.caretPosition = this.editorAdapter.caretPosition;
        this.editorPosition = this.editorAdapter.editorPosition;
        this.promise = pomise;
        try {
            const items = await pomise;
            if (this.promise === pomise) {
                this.promise = undefined;
                this.items = items;
                this.error = undefined;
            }
        }
        catch (error) {
            if (this.promise === pomise) {
                this.promise = undefined;
                this.items = defaultItems;
                this.error = error;
            }
        }
    }
    clearNow() {
        this.active = false;
        this.matchedText = defaultMatchedText;
        this.caretOffset = defaultCaretOffset;
        this.caretPosition = defaultPosition;
        this.editorPosition = defaultPosition;
        this.promise = undefined;
        this.items = defaultItems;
        this.error = undefined;
    }
    updatePositionNow() {
        if (this.active) {
            this.caretPosition = this.editorAdapter.caretPosition;
            this.editorPosition = this.editorAdapter.editorPosition;
        }
    }
    async loadItems(matchedText) {
        const items = await this._load(matchedText);
        return Array.isArray(items) ? items : defaultItems;
    }
    emitLater(eventName) {
        Promise.resolve().then(() => {
            // Ensure that there are some listeners before emitting
            // to avoid the defaul EventEmitter behaviour of throwing an error
            // when an "error" event is emitted and there are no listeners.
            if (this.listenerCount(eventName) > 0) {
                this.emit(eventName);
            }
        });
    }
}
